<template>
  <b-modal
    id="view-rental-agreement-modal"
    ref="modal"
    title="Residential Rental Agreement"
    size="lg"
    hide-footer
    @shown="initCanvas"
  >
    <div v-if="agreement" class="contract-wrapper p-2">
      <!-- Header Banner -->
      <div class="contract-header p-4 rounded mb-4 text-white d-flex justify-content-between align-items-center">
        <div>
          <span class="badge badge-light text-primary font-weight-bold mb-2">OFFICIAL LEASE CONTRACT</span>
          <h4 class="font-weight-bold mb-1">RESIDENTIAL RENTAL AGREEMENT</h4>
          <small class="opacity-75">Ref ID: {{ agreement.id }} | Currency: AED</small>
        </div>
        <div class="text-right d-flex align-items-center">
          <span class="status-pill px-3 py-2 rounded-pill font-weight-bold mr-2" :class="statusClass">
            {{ agreement.status }}
          </span>
          <a :href="pdfUrl" target="_blank" class="btn btn-sm btn-light text-danger font-weight-bold shadow-sm">
            <i class="fa fa-file-pdf-o mr-1"></i> PDF
          </a>
        </div>
      </div>

      <!-- Property Summary Card -->
      <div class="card border-0 shadow-sm mb-4 bg-light">
        <div class="card-body">
          <h6 class="font-weight-bold text-dark mb-3 border-bottom pb-2">
            <i class="fa fa-home text-primary mr-2"></i> Property & Parties
          </h6>
          <div class="row">
            <div class="col-md-6 mb-2">
              <span class="text-muted small d-block">Property Title</span>
              <strong class="text-dark">{{ agreement.propertyTitle || agreement.property_title || 'Residential Property' }}</strong>
            </div>
            <div class="col-md-3 mb-2">
              <span class="text-muted small d-block">Host / Landlord</span>
              <strong class="text-dark">{{ agreement.hostName || agreement.host_name || 'Host' }}</strong>
            </div>
            <div class="col-md-3 mb-2">
              <span class="text-muted small d-block">Tenant</span>
              <strong class="text-dark">{{ agreement.tenantName || agreement.tenant_name || 'Tenant' }}</strong>
            </div>
          </div>
        </div>
      </div>

      <!-- Financial Terms Card -->
      <div class="row mb-4">
        <div class="col-md-6">
          <div class="financial-card p-3 rounded shadow-sm text-center bg-white border">
            <span class="text-muted small font-weight-bold d-block text-uppercase">Monthly Rent</span>
            <h4 class="font-weight-bold text-primary mb-0 mt-1">AED {{ agreement.monthlyRent || agreement.monthly_rent || '0.00' }} <small class="text-muted font-weight-normal">/ month</small></h4>
          </div>
        </div>
        <div class="col-md-6">
          <div class="financial-card p-3 rounded shadow-sm text-center bg-white border">
            <span class="text-muted small font-weight-bold d-block text-uppercase">Security Deposit</span>
            <h4 class="font-weight-bold text-dark mb-0 mt-1">AED {{ agreement.securityDeposit || agreement.security_deposit || '0.00' }}</h4>
          </div>
        </div>
      </div>

      <!-- Duration Dates -->
      <div class="row mb-4">
        <div class="col-md-6">
          <div class="date-card p-3 rounded bg-white border d-flex align-items-center">
            <i class="fa fa-calendar text-info mr-3 font-24"></i>
            <div>
              <span class="text-muted small d-block">Lease Start Date</span>
              <strong class="text-dark">{{ formatShortDate(agreement.startDate || agreement.start_date) }}</strong>
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="date-card p-3 rounded bg-white border d-flex align-items-center">
            <i class="fa fa-calendar text-info mr-3 font-24"></i>
            <div>
              <span class="text-muted small d-block">Lease End Date</span>
              <strong class="text-dark">{{ formatShortDate(agreement.endDate || agreement.end_date) }}</strong>
            </div>
          </div>
        </div>
      </div>

      <!-- Terms & Conditions Bullet Points -->
      <div class="card border-0 shadow-sm mb-4">
        <div class="card-body">
          <h6 class="font-weight-bold text-dark mb-3">Terms & Special Conditions</h6>
          <div class="terms-bullet-container p-3 rounded bg-light border">
            <ul class="bullet-list pl-3 mb-0">
              <li v-for="(point, index) in parsedTerms" :key="index" class="mb-2 text-dark font-14">
                {{ point }}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Signatures Display & Signature Pad Section -->
      <div class="signature-section card border-0 shadow-sm p-3 mb-4 bg-white">
        <h6 class="font-weight-bold text-dark mb-3 border-bottom pb-2">
          <i class="fa fa-pencil-square-o text-primary mr-2"></i> Digital Signature & Execution
        </h6>

        <!-- Host and Tenant Signature Status Cards -->
        <div class="row mb-3">
          <!-- HOST Signature Card -->
          <div class="col-md-6 mb-3">
            <div class="signature-box p-3 rounded border" :class="{ 'signed-box': isHostSigned }">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <strong class="text-dark">Host / Landlord Signature</strong>
                <span v-if="isHostSigned" class="badge badge-success px-2 py-1"><i class="fa fa-check"></i> Signed</span>
                <span v-else class="badge badge-warning px-2 py-1">Pending</span>
              </div>
              <div class="signature-display p-2 text-center rounded bg-light">
                <template v-if="isHostSigned">
                  <img v-if="hostSignatureImg" :src="hostSignatureImg" alt="Host Signature" style="max-height: 45px;" />
                  <span v-else class="cursive-signature text-primary font-weight-bold">{{ hostName }}</span>
                </template>
                <span v-else class="text-muted small">Awaiting Host Signature</span>
              </div>
            </div>

            <!-- Host Signing Pad (only shown to the Host if they haven't signed yet) -->
            <div v-if="currentUserRole === 'host' && !isHostSigned" class="tenant-signing-area border rounded p-3 bg-light mt-2">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <label class="font-weight-bold text-dark mb-0">Draw Your Signature to Sign:</label>
                <button type="button" class="btn btn-sm btn-outline-secondary" @click="clearCanvas">
                  <i class="fa fa-eraser mr-1"></i> Clear Pad
                </button>
              </div>
              <div class="canvas-container bg-white border rounded">
                <canvas
                  ref="signatureCanvas"
                  width="500"
                  height="130"
                  class="w-100"
                  @mousedown="startDrawing"
                  @mousemove="draw"
                  @mouseup="stopDrawing"
                  @mouseleave="stopDrawing"
                  @touchstart.prevent="startDrawingTouch"
                  @touchmove.prevent="drawTouch"
                  @touchend.prevent="stopDrawing"
                ></canvas>
              </div>
              <div class="small text-muted mt-1">Use your mouse or touchscreen to draw your legal signature.</div>
            </div>
          </div>

          <!-- TENANT Signature Card -->
          <div class="col-md-6 mb-3">
            <div class="signature-box p-3 rounded border" :class="{ 'signed-box': isTenantSigned }">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <strong class="text-dark">Tenant Signature</strong>
                <span v-if="isTenantSigned" class="badge badge-success px-2 py-1"><i class="fa fa-check"></i> Signed</span>
                <span v-else class="badge badge-warning px-2 py-1">Pending Signature</span>
              </div>
              <div class="signature-display p-2 text-center rounded bg-light">
                <template v-if="isTenantSigned">
                  <img v-if="tenantSignatureImg" :src="tenantSignatureImg" alt="Tenant Signature" style="max-height: 45px;" />
                  <span v-else class="cursive-signature text-primary font-weight-bold">{{ tenantName }}</span>
                </template>
                <span v-else class="text-muted small">Awaiting Tenant Digital Signature</span>
              </div>
            </div>

            <!-- Tenant Signing Pad (only shown to the Tenant if they haven't signed yet) -->
            <div v-if="currentUserRole === 'tenant' && !isTenantSigned" class="tenant-signing-area border rounded p-3 bg-light mt-2">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <label class="font-weight-bold text-dark mb-0">Draw Your Signature to Sign:</label>
                <button type="button" class="btn btn-sm btn-outline-secondary" @click="clearCanvas">
                  <i class="fa fa-eraser mr-1"></i> Clear Pad
                </button>
              </div>
              <div class="canvas-container bg-white border rounded">
                <canvas
                  ref="signatureCanvas"
                  width="500"
                  height="130"
                  class="w-100"
                  @mousedown="startDrawing"
                  @mousemove="draw"
                  @mouseup="stopDrawing"
                  @mouseleave="stopDrawing"
                  @touchstart.prevent="startDrawingTouch"
                  @touchmove.prevent="drawTouch"
                  @touchend.prevent="stopDrawing"
                ></canvas>
              </div>
              <div class="small text-muted mt-1">Use your mouse or touchscreen to draw your legal signature.</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal Footer -->
      <div class="d-flex justify-content-between align-items-center pt-3 border-top">
        <button class="btn btn-outline-secondary px-4" @click="hideModal">Close</button>

        <!-- Show sign button only if the current user hasn't signed yet -->
        <div v-if="currentUserRole === 'host' && !isHostSigned">
          <button class="btn btn-success-gradient px-4" @click="signAgreement" :disabled="signing">
            <span v-if="!signing"><i class="fa fa-pencil mr-1"></i> Confirm & Sign as Host</span>
            <span v-else><i class="fa fa-spinner fa-spin mr-1"></i> Saving...</span>
          </button>
        </div>
        <div v-else-if="currentUserRole === 'tenant' && !isTenantSigned">
          <button class="btn btn-success-gradient px-4" @click="signAgreement" :disabled="signing">
            <span v-if="!signing"><i class="fa fa-pencil mr-1"></i> Confirm & Sign as Tenant</span>
            <span v-else><i class="fa fa-spinner fa-spin mr-1"></i> Saving...</span>
          </button>
        </div>
        <div v-else-if="isHostSigned && isTenantSigned" class="text-success font-weight-bold">
          <i class="fa fa-check-circle mr-1"></i> Fully Saved & Binding Agreement
        </div>
        <div v-else class="text-muted small">
          <i class="fa fa-clock-o mr-1"></i> Waiting for the other party to sign
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import API from "../api";

export default {
  name: "ViewRentalAgreementModal",
  props: {
    currentUserId: {
      default: null
    }
  },
  data() {
    return {
      agreement: null,
      currentUserRole: "tenant", // 'host' or 'tenant'
      signing: false,
      isDrawing: false,
      hasDrawn: false,
      ctx: null
    };
  },
  computed: {
    pdfUrl() {
      if (!this.agreement || !this.agreement.id) return "#";
      return API.getRentalAgreementPdfUrl(this.agreement.id);
    },
    isHostSigned() {
      if (!this.agreement) return false;
      return !!(this.agreement.hostSigned || this.agreement.host_signed || this.agreement.is_host_signed);
    },
    isTenantSigned() {
      if (!this.agreement) return false;
      return !!(this.agreement.tenantSigned || this.agreement.tenant_signed || this.agreement.status === "Completed" || this.agreement.tenant_signature_img || this.agreement.tenantSignatureImg);
    },
    hostSignatureImg() {
      if (!this.agreement) return null;
      return this.agreement.hostSignatureImg || this.agreement.host_signature_img || null;
    },
    tenantSignatureImg() {
      if (!this.agreement) return null;
      return this.agreement.tenantSignatureImg || this.agreement.tenant_signature_img || null;
    },
    hostName() {
      if (!this.agreement) return "CheapComforts Host";
      return this.agreement.hostName || this.agreement.host_name || "CheapComforts Host";
    },
    tenantName() {
      if (!this.agreement) return "Tenant";
      return this.agreement.tenantName || this.agreement.tenant_name || "Tenant";
    },
    statusClass() {
      if (!this.agreement) return "bg-secondary text-white";
      if (this.isTenantSigned || this.agreement.status === "Completed") {
        return "bg-success text-white";
      }
      return "bg-warning text-dark";
    },
    parsedTerms() {
      if (!this.agreement || (!this.agreement.terms && !this.agreement.termsList)) return [];
      if (Array.isArray(this.agreement.termsList) && this.agreement.termsList.length > 0) {
        return this.agreement.termsList.filter(p => p && p.trim());
      }
      if (typeof this.agreement.terms === 'string') {
        return this.agreement.terms
          .split('\n')
          .map(line => line.replace(/^[0-9+.\-*•\s]+/, '').trim())
          .filter(line => line.length > 0);
      }
      return [];
    }
  },
  methods: {
    getEffectiveUserId() {
      if (this.currentUserId) return this.currentUserId;
      try {
        const userStr = localStorage.getItem("user");
        if (userStr) {
          const u = JSON.parse(userStr);
          return u ? (u.id || u.user_id || (u.user && u.user.id)) : null;
        }
      } catch (e) {
        console.warn("Failed to parse user from localStorage", e);
      }
      return null;
    },
    determineUserRole(agreement, passedRole) {
      const myId = this.getEffectiveUserId();
      if (!agreement) return passedRole || "tenant";

      const hostId = agreement.host_id || agreement.hostId || null;
      const tenantId = agreement.tenant_id || agreement.tenantId || null;

      if (myId && hostId && String(myId) === String(hostId)) {
        return "host";
      }
      if (myId && tenantId && String(myId) === String(tenantId)) {
        return "tenant";
      }
      if (myId && hostId && String(myId) !== String(hostId)) {
        return "tenant";
      }
      if (passedRole === "host" || passedRole === "tenant") {
        return passedRole;
      }
      return "tenant";
    },
    show(agreementData, userRole) {
      this.agreement = { ...agreementData };
      this.currentUserRole = this.determineUserRole(this.agreement, userRole);
      this.hasDrawn = false;
      this.$refs.modal.show();

      if (agreementData && agreementData.id && !String(agreementData.id).startsWith("agr_")) {
        API.getRentalAgreement(
          agreementData.id,
          (res) => {
            if (res && res.agreement) {
              this.agreement = {
                ...this.agreement,
                ...res.agreement,
                pdf_url: res.pdf_url || this.agreement.pdf_url
              };
              // Re-evaluate role after fresh API data load
              this.currentUserRole = this.determineUserRole(this.agreement, userRole);
            }
          },
          (err) => {
            console.error("Failed to load fresh agreement data", err);
          }
        );
      }
    },
    formatShortDate(dateStr) {
      if (!dateStr) return '';
      if (typeof dateStr === 'string' && dateStr.includes('T')) {
        return dateStr.split('T')[0];
      }
      return dateStr;
    },
    hideModal() {
      this.$refs.modal.hide();
    },
    getCanvas() {
      const ref = this.$refs.signatureCanvas;
      if (Array.isArray(ref)) return ref[0];
      return ref;
    },
    initCanvas() {
      this.$nextTick(() => {
        const canvas = this.getCanvas();
        if (canvas) {
          this.ctx = canvas.getContext("2d");
          this.ctx.strokeStyle = "#000000";
          this.ctx.lineWidth = 2.5;
          this.ctx.lineCap = "round";
          this.clearCanvas();
        }
      });
    },
    clearCanvas() {
      const canvas = this.getCanvas();
      if (canvas && this.ctx) {
        this.ctx.clearRect(0, 0, canvas.width, canvas.height);
        this.hasDrawn = false;
      }
    },
    getCanvasCoords(e) {
      const canvas = this.getCanvas();
      if (!canvas) return { x: 0, y: 0 };
      const rect = canvas.getBoundingClientRect();
      return {
        x: (e.clientX - rect.left) * (canvas.width / rect.width),
        y: (e.clientY - rect.top) * (canvas.height / rect.height)
      };
    },
    startDrawing(e) {
      this.isDrawing = true;
      const coords = this.getCanvasCoords(e);
      if (this.ctx) {
        this.ctx.beginPath();
        this.ctx.moveTo(coords.x, coords.y);
      }
    },
    draw(e) {
      if (!this.isDrawing) return;
      const coords = this.getCanvasCoords(e);
      this.ctx.lineTo(coords.x, coords.y);
      this.ctx.stroke();
      this.hasDrawn = true;
    },
    startDrawingTouch(e) {
      if (e.touches && e.touches[0]) {
        this.startDrawing(e.touches[0]);
      }
    },
    drawTouch(e) {
      if (e.touches && e.touches[0]) {
        this.draw(e.touches[0]);
      }
    },
    stopDrawing() {
      this.isDrawing = false;
    },
    signAgreement() {
      this.signing = true;
      let signatureDataUrl = null;
      const canvas = this.getCanvas();
      if (canvas && this.hasDrawn) {
        signatureDataUrl = canvas.toDataURL("image/png");
      }

      const isHost = this.currentUserRole === "host";
      const payload = isHost
        ? {
            host_signature_img: signatureDataUrl,
            role: "host"
          }
        : {
            tenant_signature_img: signatureDataUrl,
            tenant_name: this.agreement.tenantName || this.agreement.tenant_name,
            role: "tenant"
          };

      if (this.agreement.id && !String(this.agreement.id).startsWith("agr_")) {
        API.signRentalAgreement(
          this.agreement.id,
          payload,
          (res) => {
            this.signing = false;
            if (isHost) {
              this.agreement.hostSigned = true;
              this.agreement.host_signed = true;
              this.agreement.hostSignatureImg = signatureDataUrl;
            } else {
              this.agreement.tenantSigned = true;
              this.agreement.tenant_signed = true;
              this.agreement.tenantSignatureImg = signatureDataUrl;
              this.agreement.status = "Completed";
            }
            if (res.pdf_url) {
              this.agreement.pdf_url = res.pdf_url;
            }
            this.$emit("agreement-signed", this.agreement);
          },
          (err) => {
            this.signing = false;
            console.error("Signature submission failed", err);
            if (isHost) {
              this.agreement.hostSigned = true;
              this.agreement.hostSignatureImg = signatureDataUrl;
            } else {
              this.agreement.tenantSigned = true;
              this.agreement.status = "Completed";
              this.agreement.tenantSignatureImg = signatureDataUrl;
            }
            this.$emit("agreement-signed", this.agreement);
          }
        );
      } else {
        setTimeout(() => {
          this.signing = false;
          if (isHost) {
            this.agreement.hostSigned = true;
            this.agreement.hostSignatureImg = signatureDataUrl;
          } else {
            this.agreement.tenantSigned = true;
            this.agreement.status = "Completed";
            this.agreement.tenantSignatureImg = signatureDataUrl;
          }
          this.$emit("agreement-signed", this.agreement);
        }, 400);
      }
    }
  }
};
</script>

<style scoped>
.contract-header {
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
}
.bullet-list li {
  line-height: 1.6;
}
.font-14 {
  font-size: 14px;
}
.font-24 {
  font-size: 24px;
}
.cursive-signature {
  font-family: 'Brush Script MT', 'Dancing Script', cursive;
  font-size: 20px;
}
.signed-box {
  background-color: #f6ffed;
  border-color: #b7eb8f !important;
}
.canvas-container {
  touch-action: none;
  cursor: crosshair;
}
.btn-success-gradient {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  border: none;
  color: white;
  font-weight: 600;
  border-radius: 8px;
  padding: 10px 24px;
}
.btn-success-gradient:hover {
  opacity: 0.92;
  color: white;
}
</style>
